import firebase from "firebase/compat/app";
import "firebase/compat/functions";

function facilityCardCallable(name) {
  return firebase.app().functions("us-central1").httpsCallable(name);
}

/**
 * Map NP-3a PIN validation messages to plain patient-facing sentences.
 * @param {unknown} error
 * @returns {string|null}
 */
export function mapFacilityCardPinError(error) {
  const message = String(error?.message || error?.details || "").trim();
  if (!message) return null;
  if (message.includes("exactly 6 digits")) {
    return "The PIN must be exactly 6 digits.";
  }
  if (message.includes("all the same digit")) {
    return "The PIN must not be the same digit six times.";
  }
  if (message.includes("sequential run")) {
    return "The PIN must not be a straight run such as 123456 or 654321.";
  }
  if (message.includes("date of birth")) {
    return "The PIN must not match the date of birth.";
  }
  if (error?.code === "functions/invalid-argument" || message) {
    return message.replace(/^Firebase:\s*/i, "").replace(/\s*\([^)]*\)\.?\s*$/, "").trim() || null;
  }
  return null;
}

/**
 * @param {object} params
 * @returns {Promise<{ patientUid?: string, possibleDuplicate?: boolean, candidates?: Array }>}
 */
export async function createPatientForFacilityCard({
  sessionId,
  givenName,
  familyName,
  dateOfBirth,
  idType,
  sex,
  preferredLanguage,
  confirmNoDuplicate = false,
} = {}) {
  const callable = facilityCardCallable("createPatientForFacilityCard");
  const result = await callable({
    sessionId,
    givenName,
    familyName,
    dateOfBirth,
    idType,
    ...(sex ? { sex } : {}),
    ...(preferredLanguage ? { preferredLanguage } : {}),
    ...(confirmNoDuplicate ? { confirmNoDuplicate: true } : {}),
  });
  return result?.data || {};
}

/**
 * @param {object} params
 * @returns {Promise<{
 *   displayCode?: string,
 *   expiresAt?: string,
 *   facilityName?: string,
 *   hasActiveCard?: boolean,
 *   lastUsedAt?: unknown,
 * }>}
 */
export async function issueFacilityAccessCard({
  sessionId,
  patientUid,
  pin,
  idType,
  replaceExisting = false,
} = {}) {
  const callable = facilityCardCallable("issueFacilityAccessCard");
  const result = await callable({
    sessionId,
    patientUid,
    pin,
    idType,
    ...(replaceExisting ? { replaceExisting: true } : {}),
  });
  return result?.data || {};
}

/**
 * Normalize a facility access code the same way the server does
 * (uppercase, strip spaces and dashes).
 * @param {unknown} value
 * @returns {string}
 */
export function normalizeFacilityAccessCode(value) {
  return String(value || "")
    .toUpperCase()
    .replace(/[\s-]+/g, "")
    .trim();
}

/**
 * Patient sign-in with card code + PIN. Returns a Firebase custom token.
 * @param {{ code: string, pin: string }} params
 * @returns {Promise<{ customToken: string }>}
 */
export async function signInWithFacilityAccessCard({ code, pin } = {}) {
  const callable = facilityCardCallable("signInWithFacilityAccessCard");
  const result = await callable({
    code: normalizeFacilityAccessCode(code),
    pin,
  });
  return result?.data || {};
}

/**
 * @param {object} params
 * @returns {Promise<object>}
 */
export async function manageFacilityAccessCard({
  sessionId,
  patientUid,
  action,
  idType,
  pin,
  currentPin,
  newPin,
} = {}) {
  const callable = facilityCardCallable("manageFacilityAccessCard");
  const payload = {
    sessionId,
    patientUid,
    action,
  };
  if (idType) payload.idType = idType;
  if (pin != null) payload.pin = pin;
  if (currentPin != null) payload.currentPin = currentPin;
  if (newPin != null) payload.newPin = newPin;
  const result = await callable(payload);
  return result?.data || {};
}

/**
 * Patient self-service PIN change (requires facilityCard claim server-side).
 * @param {{ patientUid: string, currentPin: string, newPin: string }} params
 * @returns {Promise<{ ok?: boolean }>}
 */
export async function changeFacilityAccessCardPin({
  patientUid,
  currentPin,
  newPin,
} = {}) {
  return manageFacilityAccessCard({
    patientUid,
    action: "changePin",
    currentPin,
    newPin,
  });
}

/**
 * Status for staff panel. Maps not-found to `{ status: "none" }`.
 * When a card is missing, the callable now returns `{ status: "none",
 * consentDueLinks }` directly; the not-found mapping remains for older
 * deployments.
 * @param {{ sessionId: string, patientUid: string }} params
 * @returns {Promise<{
 *   status: string,
 *   issuedAt?: unknown,
 *   expiresAt?: unknown,
 *   lastUsedAt?: unknown,
 *   lastUsedInstitutionId?: string|null,
 *   consentDueLinks?: Array<{ institutionName: string, dueAt: string }>,
 * }>}
 */
export async function getCardStatusForPatient({ sessionId, patientUid } = {}) {
  try {
    return await manageFacilityAccessCard({
      sessionId,
      patientUid,
      action: "getCardStatusForPatient",
    });
  } catch (err) {
    const code = String(err?.code || "");
    const message = String(err?.message || "");
    if (
      code === "functions/not-found" ||
      message.includes("No facility access card found")
    ) {
      return { status: "none", consentDueLinks: [] };
    }
    throw err;
  }
}

export async function unlockFacilityAccessCard(params) {
  return manageFacilityAccessCard({ ...params, action: "unlock" });
}

export async function resetFacilityAccessCardPin(params) {
  return manageFacilityAccessCard({ ...params, action: "resetPin" });
}

export async function revokeFacilityAccessCard(params) {
  return manageFacilityAccessCard({ ...params, action: "revoke" });
}

export async function reissueFacilityAccessCard(params) {
  return manageFacilityAccessCard({ ...params, action: "reissue" });
}
